import { SolText } from '../models/core/sol-text';
import { SolValidationMessage } from '../models/core/sol-validation-message';

/**
 * Turns a rejected preference write into the message its surface shows, so the in-context surfaces
 * and the navbar toast cannot drift apart.
 */
export class PreferenceTools {
  private static readonly REFUSED_STATUS = 403;
  private static readonly REFUSED_KEY = 'sol-preference.refused-by-authority';
  private static readonly FAILED_KEY = 'sol-preference.save-failed';

  public static toFailureMessage(error: unknown): SolValidationMessage {
    return {
      type: PreferenceTools.toFailureType(error),
      text: SolText.of(PreferenceTools.toFailureKey(error)),
    };
  }

  public static toFailureKey(error: unknown): string {
    return PreferenceTools.isRefused(error) ? PreferenceTools.REFUSED_KEY : PreferenceTools.FAILED_KEY;
  }

  public static toFailureType(error: unknown): 'info' | 'error' {
    return PreferenceTools.isRefused(error) ? 'info' : 'error';
  }

  /** Narrows a stored preference value to a string; a wrong shape reads as absent, not as `''`. */
  public static toStoredString(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined;
  }

  /** Narrows a stored preference value to a boolean; a wrong shape reads as absent, not as `false`. */
  public static toStoredBoolean(value: unknown): boolean | undefined {
    return typeof value === 'boolean' ? value : undefined;
  }

  /** Narrows a stored preference value to a number; a wrong shape reads as absent, not as `NaN`. */
  public static toStoredNumber(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
  }

  private static isRefused(error: unknown): boolean {
    return (error as { status?: number } | null)?.status === PreferenceTools.REFUSED_STATUS;
  }
}
